<script setup>
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

defineOptions({
  name: 'EmailTemplatesSettings',
});

const { t } = useI18n();
const router = useRouter();

const isLoading = ref(true);
const templates = ref([]);
const searchQuery = ref('');

const filteredTemplates = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return templates.value;

  return templates.value.filter(
    template =>
      template.label.toLowerCase().includes(query) ||
      template.group.toLowerCase().includes(query) ||
      template.name.toLowerCase().includes(query)
  );
});

const groupedTemplates = computed(() => {
  return filteredTemplates.value.reduce((groups, template) => {
    if (!groups[template.group]) {
      groups[template.group] = [];
    }
    groups[template.group].push(template);
    return groups;
  }, {});
});

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await EmailTemplatesAPI.get();
    templates.value = data.payload || [];
  } catch {
    useAlert(t('EMAIL_TEMPLATES.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const openEditor = template => {
  router.push({
    name: 'email_templates_edit',
    params: { templateId: template.id },
  });
};

const statusLabel = template => {
  if (!template.editable) {
    return t('EMAIL_TEMPLATES.LIST.READ_ONLY');
  }
  return template.customized
    ? t('EMAIL_TEMPLATES.LIST.STATUS.CUSTOMIZED')
    : t('EMAIL_TEMPLATES.LIST.STATUS.DEFAULT');
};

const statusClass = template => {
  if (!template.editable) {
    return 'bg-n-slate-3 text-n-slate-11';
  }
  return template.customized
    ? 'bg-n-brand/10 text-n-brand'
    : 'bg-n-slate-3 text-n-slate-11';
};

onMounted(fetchTemplates);
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('EMAIL_TEMPLATES.LIST.404')"
    :no-records-found="!filteredTemplates.length"
    :is-loading="isLoading"
    :loading-message="$t('EMAIL_TEMPLATES.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('EMAIL_TEMPLATES.HEADER')"
        :description="$t('EMAIL_TEMPLATES.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col w-full gap-8">
        <input
          v-model="searchQuery"
          type="search"
          class="w-full max-w-md px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="$t('EMAIL_TEMPLATES.SEARCH_PLACEHOLDER')"
        />

        <section
          v-for="(groupTemplates, groupName) in groupedTemplates"
          :key="groupName"
          class="flex flex-col gap-2"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ groupName }}
          </h3>

          <div class="overflow-x-auto border rounded-lg border-n-weak">
            <table class="w-full table-fixed">
              <colgroup>
                <col class="w-auto" />
                <col class="w-44" />
                <col class="w-20" />
              </colgroup>
              <thead>
                <tr class="border-b border-n-weak bg-n-alpha-1">
                  <th
                    class="px-4 py-3 text-xs font-semibold text-left text-n-slate-11"
                  >
                    {{ $t('EMAIL_TEMPLATES.LIST.TABLE_HEADER.NAME') }}
                  </th>
                  <th
                    class="px-4 py-3 text-xs font-semibold text-left text-n-slate-11"
                  >
                    {{ $t('EMAIL_TEMPLATES.LIST.TABLE_HEADER.STATUS') }}
                  </th>
                  <th
                    class="px-4 py-3 text-xs font-semibold text-right text-n-slate-11"
                  >
                    {{ $t('EMAIL_TEMPLATES.LIST.TABLE_HEADER.ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="template in groupTemplates"
                  :key="template.id"
                  class="hover:bg-n-alpha-1"
                >
                  <td class="px-4 py-3 text-sm font-medium truncate text-n-slate-12">
                    {{ template.label }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap"
                      :class="statusClass(template)"
                    >
                      {{ statusLabel(template) }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex justify-end">
                      <Button
                        v-tooltip.top="
                          template.editable
                            ? $t('EMAIL_TEMPLATES.LIST.EDIT')
                            : $t('EMAIL_TEMPLATES.LIST.VIEW')
                        "
                        :icon="
                          template.editable ? 'i-lucide-pen' : 'i-lucide-eye'
                        "
                        slate
                        xs
                        faded
                        @click="openEditor(template)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
